import React, { Suspense, lazy } from "react";
import { Route, BrowserRouter as Router, Routes } from "react-router-dom";
import Footer from "./components/Footer";
import Navbar from "./components/Navbar";
import ScrollToTop from "./ScrollToTop";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import PageTransition from "./components/ui/PageTransition";
import SkeletonLoader from "./components/ui/SkeletonLoader";

// Lazy load all page components for better performance
const Home = lazy(() => import("./pages/Home"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Industries = lazy(() => import("./pages/Industries"));
const ProductsAndServices = lazy(() => import("./pages/Products&Services"));
const ProductDetailPage = lazy(() => import("./components/products$Services/ProductDetailPage"));
const Resources = lazy(() => import("./pages/Resources"));
const Booking = lazy(() => import("./pages/Booking"));
const AdminBookings = lazy(() => import("./pages/AdminBookings"));
const NotFound = lazy(() => import("./pages/NotFound"));
const PrivacyPolicyAndTerms = lazy(() => import("./components/PrivacyPolicyAndTerms"));

// Lazy load resource sub-components
const Blogs = lazy(() => import("./components/resources/Blogs"));
const FAQs = lazy(() => import("./components/resources/FAQs"));
const Guides = lazy(() => import("./components/resources/Guides"));
const Partnership = lazy(() => import("./components/resources/Partnership"));

// Loading fallback component
const LoadingFallback = () => (
  <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900" role="status" aria-label="Loading page">
    <SkeletonLoader variant="card" count={1} />
    <p className="mt-4 text-gray-600 dark:text-gray-400 font-medium">Loading...</p>
    <span className="sr-only">Loading...</span>
  </div>
);

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <ErrorBoundary>
        <Router>
          <ScrollToTop /> 
          <Navbar />
          <Suspense fallback={<LoadingFallback />}>
            <PageTransition variant="fade">
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/about" element={<About />} />
                <Route path="/services" element={<ProductsAndServices />} />
                <Route path="/products/:productId" element={<ProductDetailPage />} />
                <Route path="/industries" element={<Industries />} />
                <Route path="/privacy-policy" element={<PrivacyPolicyAndTerms />} />
                
                <Route path="/resources" element={<Resources />}>
                  <Route index element={<Blogs />} />
                  <Route path="blogs" element={<Blogs />} />
                  <Route path="faqs" element={<FAQs />} />
                  <Route path="guides" element={<Guides />} />
                  <Route path="partnership" element={<Partnership />} />
                </Route>
                <Route path="/contact" element={<Contact />} />
                <Route path="/booking" element={<Booking />} />
                <Route path="/admin/bookings" element={<AdminBookings />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </PageTransition>
          </Suspense>
          <Footer />
        </Router>
      </ErrorBoundary>
    </ThemeProvider>
  );
};

export default App;
